
import { Component, Vue } from "vue-property-decorator";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";
import * as Model from "@gigalot/data-models";
import lodash from "lodash";
import moment from "moment";

interface HistoryAllocationRow {
  key: string;
  sequence: number | null;
  ration: string;
  percentage: number | null;
  feedAllocated: number | null;
  actualFed: number | null;
  variance: number | null;
  active: boolean;
}

interface HistoryRow {
  key: string;
  date: number;
  kraalId: string;
  kraalGuid: string;
  owner: string;
  description: string;
  avgMass: number | null;
  gender: string;
  animalCount: number | null;
  ration: string;
  rationColor: string;
  blending: boolean;
  dryMatterPct: number | null;
  score: number | null;
  callDry: number | null;
  fedDry: number | null;
  callWet: number | null;
  fedWet: number | null;
  totalAllocatedWet: number | null;
  totalFedWet: number | null;
  varianceWet: number | null;
  variancePct: number | null;
  sessions: string;
  patched: boolean;
  detailRows: HistoryAllocationRow[];
}

interface HistoryRowInternal extends HistoryRow {
  hasPlan: boolean;
  hasCrib: boolean;
  hasSchedule: boolean;
}

@Component({
  components: {
    BreadcrumbTrail,
  },
})
export default class History extends Vue {
  loading = false;
  errorText = "";
  successText = "";

  rows: HistoryRow[] = [];
  owners: Model.CustomFeeder[] = [];

  selectedKraal: string | null = null;
  selectedLotDescription: string | null = null;
  search = "";
  startDate = "";
  endDate = "";
  startDateMenu = false;
  endDateMenu = false;
  selectedRowKey = "";
  reUploadDialog = false;
  reUploadBusy = false;

  headers = [
    { text: "Kraal", value: "kraalId" },
    { text: "Date", value: "date" },
    { text: "Owner", value: "owner" },
    { text: "Lot", value: "description" },
    { text: "Avg kraal mass", value: "avgMass", align: "end" },
    { text: "Gender", value: "gender" },
    { text: "# Animals", value: "animalCount", align: "end" },
    { text: "Ration", value: "ration" },
    { text: "Blending", value: "blending", align: "center", sortable: false },
    { text: "DM%", value: "dryMatterPct", align: "end" },
    { text: "Score", value: "score", align: "center" },
    { text: "Call (dry)", value: "callDry", align: "end" },
    { text: "Fed (dry)", value: "fedDry", align: "end" },
    { text: "Call (wet)", value: "callWet", align: "end" },
    { text: "Fed (wet)", value: "fedWet", align: "end" },
    { text: "Total allocated (wet)", value: "totalAllocatedWet", align: "end" },
    { text: "Total fed (wet)", value: "totalFedWet", align: "end" },
    { text: "Variance (wet)", value: "varianceWet", align: "end" },
    { text: "Variance %", value: "variancePct", align: "end" },
    { text: "Sessions", value: "sessions", sortable: false },
    { text: "Patched", value: "patched", sortable: false },
  ];

  detailHeaders = [
    { text: "Session", value: "sequence", align: "center" },
    { text: "Ration", value: "ration" },
    { text: "Pct", value: "percentage", align: "end" },
    { text: "Allocated (wet)", value: "feedAllocated", align: "end" },
    { text: "Fed (wet)", value: "actualFed", align: "end" },
    { text: "Variance (wet)", value: "variance", align: "end" },
    { text: "Active", value: "active", align: "center", sortable: false },
  ];

  footerProps = {
    "items-per-page-options": [25, 50, 100],
  };

  mounted() {
    this.loadHistory();
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "History", disabled: true });
  }

  get gigalotAdmin(): boolean {
    return this.$store.getters["user/isUserGigalotAdmin"]();
  }

  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get tableHeight(): number {
    return this.$vuetify.breakpoint.mdAndUp ? 520 : 720;
  }

  get kraalOptions(): string[] {
    return Array.from(
      new Set(
        this.rows
          .map((item) => item.kraalId)
          .filter((item) => !!item)
          .sort()
      )
    );
  }

  get lotOptions(): string[] {
    return Array.from(
      new Set(
        this.rows
          .map((item) => item.description.trim())
          .filter((item) => !!item)
      )
    ).sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }));
  }

  get filteredRows(): HistoryRow[] {
    const startBoundary = this.getDateBoundary(this.startDate, false);
    const endBoundary = this.getDateBoundary(this.endDate, true);
    const searchValue = this.search.trim().toLowerCase();

    return this.rows.filter((item) => {
      if (this.selectedKraal && item.kraalId !== this.selectedKraal) return false;
      if (this.selectedLotDescription && item.description.trim() !== this.selectedLotDescription) return false;
      if (startBoundary !== null && item.date < startBoundary) return false;
      if (endBoundary !== null && item.date > endBoundary) return false;
      if (!searchValue) return true;

      const haystack = [item.kraalId, item.owner, item.description, item.ration, item.gender]
        .filter((value) => !!value)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchValue);
    });
  }

  get selectedRow(): HistoryRow | undefined {
    return this.filteredRows.find((item) => item.key === this.selectedRowKey) ?? this.filteredRows[0];
  }

  get selectedUploadDate(): string {
    if (!this.selectedRow?.date) return "";
    return moment(this.selectedRow.date).format("YYYY-MM-DD");
  }

  get selectedUploadDateLabel(): string {
    return this.selectedUploadDate || "the selected date";
  }

  get latestVisibleDate(): string {
    if (this.filteredRows.length === 0) return "--";
    return this.formatDate(this.filteredRows[0].date);
  }

  get dateRangeSummary(): string {
    if (!this.startDate && !this.endDate) return "All dates";
    if (this.startDate && this.endDate) return `${this.startDate} to ${this.endDate}`;
    if (this.startDate) return `From ${this.startDate}`;
    return `Up to ${this.endDate}`;
  }

  async loadHistory() {
    this.loading = true;
    this.errorText = "";
    this.successText = "";

    try {
      const gql = `query History($guid: String!) {
        CribScoreHistory(guid: $guid) {
          guid
          date
          cribScores {
            guid
            score
            kraalId
            kraalGuid
            readingDate
            call
            status
            ration
            rationGuid
            actualFed
            color
            gender
            animalCount
          }
        }
        FeedScheduleHistory(guid: $guid) {
          guid
          date
          feedingScheduleItems {
            guid
            kraalId
            kraalGuid
            ration
            rationGuid
            feedAllocated
            actualFed
            sequence
            activeFeed
            feedAllocations {
              feedAllocated
              actualFed
              active
              allocated
              percentage
              scheduledAllocatedFeed
              ration
              rationGuid
            }
          }
        }
        FeedingPlanHistory(guid: $guid) {
          guid
          date
          FeedingPlanItems {
            guid
            kraalId
            kraalGuid
            currentRation {
              guid
              ration
              rationGuid
              colour
              blendPlanGuid
            }
            numberAnimals
            owner
            gender
            kraalAverageMass
            kraalAverageMassFloat
            lotDescription
            lotNote
          }
        }
        customFeeders(guid: $guid) {
          guid
          name
          active
        }
      }`;

      const response = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
      });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((error: any) => error.message).join(", "));
      }

      this.owners = (response?.data?.customFeeders ?? []) as Model.CustomFeeder[];
      this.rows = this.buildRows(
        (response?.data?.CribScoreHistory ?? []) as Model.CribReadingPlan[],
        (response?.data?.FeedScheduleHistory ?? []) as Model.FeedingSchedule[],
        (response?.data?.FeedingPlanHistory ?? []) as Model.FeedingPlan[]
      );

      if (this.selectedKraal && !this.kraalOptions.includes(this.selectedKraal)) {
        this.selectedKraal = null;
      }
      if (this.selectedLotDescription && !this.lotOptions.includes(this.selectedLotDescription)) {
        this.selectedLotDescription = null;
      }
      this.selectedRowKey = this.selectedRow?.key ?? "";
    } catch (err) {
      console.log("loadHistory -> error:", err);
      this.errorText = `Error downloading history: ${err}`;
      this.rows = [];
      this.selectedRowKey = "";
    } finally {
      this.loading = false;
    }
  }

  clearFilters() {
    this.selectedKraal = null;
    this.selectedLotDescription = null;
    this.search = "";
    this.startDate = "";
    this.endDate = "";
    this.selectedRowKey = this.rows[0]?.key ?? "";
  }

  selectRow(item: HistoryRow) {
    this.selectedRowKey = item.key;
  }

  openReUploadDialog() {
    if (!this.selectedRow || !this.gigalotAdmin) return;
    this.errorText = "";
    this.successText = "";
    this.reUploadDialog = true;
  }

  closeReUploadDialog() {
    if (this.reUploadBusy) return;
    this.reUploadDialog = false;
  }

  async confirmReUpload() {
    if (!this.selectedUploadDate || !this.gigalotAdmin) return;

    this.reUploadBusy = true;
    this.errorText = "";
    this.successText = "";

    try {
      const gql = `mutation ReUploadHistoricalData($guid: String!, $date: String!) {
        reUploadHistoricalData(guid: $guid, date: $date)
      }`;

      const response = await this.$store.dispatch("graphQl", {
        gql,
        variables: {
          guid: this.$store.state.user.location.guid,
          date: this.selectedUploadDate,
        },
      });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((error: any) => error.message).join(", "));
      }

      this.successText = response?.data?.reUploadHistoricalData || `Re-upload started for ${this.selectedUploadDate}.`;
      this.reUploadDialog = false;
    } catch (err) {
      console.log("confirmReUpload -> error:", err);
      this.errorText = `Error re-uploading history: ${err}`;
    } finally {
      this.reUploadBusy = false;
    }
  }

  getRowClass(item: HistoryRow) {
    if (!item) return "";
    const classNames = [];
    if (this.selectedRow?.key === item.key) classNames.push("history-row--selected");
    if (item.patched) classNames.push("history-row--patched");
    return classNames.join(" ");
  }

  formatDate(value: number | null) {
    if (!value) return "--";
    return moment(value).format("DD MMM YYYY");
  }

  formatNumber(value: number | null, decimals: number = 2) {
    if (value === null || value === undefined || Number.isNaN(value)) return "--";
    return Number(value).toFixed(decimals);
  }

  formatPercent(value: number | null, decimals: number = 1) {
    if (value === null || value === undefined || Number.isNaN(value)) return "--";
    return `${Number(value).toFixed(decimals)}%`;
  }

  formatScore(score: number | null) {
    if (score === null || score === undefined) return "--";
    if (score < 0) return "#";
    return `${score}`;
  }

  getGenderColor(gender: string) {
    if (gender === "Male") return "light-blue";
    if (gender === "Female") return "pink";
    return "grey";
  }

  getRationColor(item: HistoryRow) {
    return item.rationColor ? item.rationColor.toLowerCase() : "blue-grey";
  }

  getScoreColor(score: number | null) {
    if (score === null || score === undefined) return "grey";
    if (score < 0) return "blue-grey";
    if (score <= 1) return "green";
    if (score === 2) return "amber darken-1";
    return "red";
  }

  getVarianceClass(value: number | null) {
    if (value === null || value === undefined) return "";
    if (value > 0) return "history-value--high";
    if (value < 0) return "history-value--low";
    return "history-value--neutral";
  }

  getDryMatterClass(value: number | null) {
    return "history-value-yellow";
  }

  private buildRows(
    cribHistory: Model.CribReadingPlan[],
    scheduleHistory: Model.FeedingSchedule[],
    planHistory: Model.FeedingPlan[]
  ): HistoryRow[] {
    const rowMap = new Map<string, HistoryRowInternal>();

    const ensureRow = (date: number, kraalId: string, kraalGuid: string) => {
      const key = this.getRowKey(date, kraalId);
      const existing = rowMap.get(key);
      if (existing) return existing;

      const row: HistoryRowInternal = {
        key,
        date,
        kraalId,
        kraalGuid,
        owner: "",
        description: "",
        avgMass: null,
        gender: "",
        animalCount: null,
        ration: "",
        rationColor: "",
        blending: false,
        dryMatterPct: null,
        score: null,
        callDry: null,
        fedDry: null,
        callWet: null,
        fedWet: null,
        totalAllocatedWet: null,
        totalFedWet: null,
        varianceWet: null,
        variancePct: null,
        sessions: "",
        patched: false,
        detailRows: [],
        hasPlan: false,
        hasCrib: false,
        hasSchedule: false,
      };
      rowMap.set(key, row);
      return row;
    };

    for (const planDay of planHistory ?? []) {
      for (const item of planDay?.FeedingPlanItems ?? []) {
        const date = this.resolveDayDate(planDay?.date, item?.time);
        const kraalId = item?.kraalId ?? "";
        if (!date || !kraalId) continue;
        const row = ensureRow(date, kraalId, item?.kraalGuid ?? "");
        row.owner = this.lookupOwner(item?.owner) || row.owner;
        row.description = item?.lotDescription || item?.lotNote || row.description;
        row.avgMass = this.firstNumber(row.avgMass, item?.kraalAverageMass, item?.kraalAverageMassFloat);
        row.gender = item?.gender || row.gender;
        row.animalCount = this.firstNumber(row.animalCount, item?.numberAnimals);
        row.ration = item?.currentRation?.ration || row.ration;
        row.rationColor = item?.currentRation?.colour || row.rationColor;
        row.blending = Boolean(item?.currentRation?.blendPlanGuid || row.blending);
        row.hasPlan = true;
      }
    }

    for (const cribDay of cribHistory ?? []) {
      const groupedItems = lodash.groupBy(cribDay?.cribScores ?? [], (item) => item?.kraalId || item?.kraalGuid || "");
      for (const key of Object.keys(groupedItems)) {
        if (!key) continue;
        const grouped = groupedItems[key];
        const latest = lodash.maxBy(grouped, (item) => item?.readingDate ?? 0) ?? grouped[0];
        const date = this.resolveDayDate(cribDay?.date, latest?.readingDate);
        const kraalId = latest?.kraalId ?? "";
        if (!date || !kraalId) continue;
        const row = ensureRow(date, kraalId, latest?.kraalGuid ?? "");
        row.score = this.firstNumber(row.score, latest?.score);
        row.callDry = this.firstNumber(row.callDry, latest?.call);
        row.fedDry = this.firstNumber(row.fedDry, latest?.actualFed);
        row.animalCount = this.firstNumber(row.animalCount, latest?.animalCount);
        row.gender = latest?.gender || row.gender;
        row.ration = row.ration || latest?.ration || "";
        row.rationColor = row.rationColor || latest?.color || "";
        row.hasCrib = true;
      }
    }

    for (const scheduleDay of scheduleHistory ?? []) {
      const groupedItems = lodash.groupBy(scheduleDay?.feedingScheduleItems ?? [], (item) => item?.kraalId || item?.kraalGuid || "");
      for (const key of Object.keys(groupedItems)) {
        if (!key) continue;
        const grouped = groupedItems[key];
        const sample = grouped[0];
        const date = this.resolveDayDate(scheduleDay?.date, sample?.time);
        const kraalId = sample?.kraalId ?? "";
        if (!date || !kraalId) continue;
        const row = ensureRow(date, kraalId, sample?.kraalGuid ?? "");

        const totalAllocatedWet = grouped.reduce((sum, item) => sum + (this.toNumber(item?.feedAllocated) ?? 0), 0);
        const totalFedWet = grouped.reduce((sum, item) => sum + (this.toNumber(item?.actualFed) ?? 0), 0);

        row.totalAllocatedWet = totalAllocatedWet;
        row.totalFedWet = totalFedWet;
        row.ration = row.ration || sample?.ration || "";
        row.detailRows = this.buildDetailRows(grouped);
        row.sessions = this.buildSessionSummary(row.detailRows);
        row.hasSchedule = true;
      }
    }

    const rows = Array.from(rowMap.values()).map((row) => this.finalizeRow(row));
    return lodash.orderBy(rows, ["date", "kraalId"], ["desc", "asc"]);
  }

  private finalizeRow(row: HistoryRowInternal): HistoryRow {
    const animalCount = this.toNumber(row.animalCount);
    const totalAllocatedWet = this.toNumber(row.totalAllocatedWet);
    const totalFedWet = this.toNumber(row.totalFedWet);
    const callDry = this.toNumber(row.callDry);
    const fedDry = this.toNumber(row.fedDry);

    if (animalCount && totalAllocatedWet !== null) {
      row.callWet = totalAllocatedWet / animalCount;
    }
    if (animalCount && totalFedWet !== null) {
      row.fedWet = totalFedWet / animalCount;
    }
    if (totalAllocatedWet !== null || totalFedWet !== null) {
      row.varianceWet = (totalFedWet ?? 0) - (totalAllocatedWet ?? 0);
      row.variancePct = totalAllocatedWet ? ((row.varianceWet ?? 0) / totalAllocatedWet) * 100 : null;
    }
    row.dryMatterPct = this.calculateDryMatterPct(callDry, row.callWet, fedDry, row.fedWet);
    row.patched = !(row.hasPlan && row.hasCrib && row.hasSchedule);

    return {
      key: row.key,
      date: row.date,
      kraalId: row.kraalId,
      kraalGuid: row.kraalGuid,
      owner: row.owner,
      description: row.description,
      avgMass: row.avgMass,
      gender: row.gender,
      animalCount: row.animalCount,
      ration: row.ration,
      rationColor: row.rationColor,
      blending: row.blending,
      dryMatterPct: row.dryMatterPct,
      score: row.score,
      callDry: row.callDry,
      fedDry: row.fedDry,
      callWet: row.callWet,
      fedWet: row.fedWet,
      totalAllocatedWet: row.totalAllocatedWet,
      totalFedWet: row.totalFedWet,
      varianceWet: row.varianceWet,
      variancePct: row.variancePct,
      sessions: row.sessions,
      patched: row.patched,
      detailRows: row.detailRows,
    };
  }

  private buildDetailRows(items: Model.FeedingScheduleItem[]): HistoryAllocationRow[] {
    const rows: HistoryAllocationRow[] = [];

    items.forEach((item, itemIndex) => {
      const allocations: Array<Model.FeedingScheduleItemAllocation | undefined> =
        Array.isArray(item?.feedAllocations) && item.feedAllocations.length > 0 ? item.feedAllocations : [undefined];
      allocations.forEach((allocation: Model.FeedingScheduleItemAllocation | undefined, allocationIndex: number) => {
        const feedAllocated = this.toNumber(allocation?.feedAllocated ?? item?.feedAllocated);
        const actualFed = this.toNumber(allocation?.actualFed ?? item?.actualFed);
        rows.push({
          key: `${item?.guid || itemIndex}-${allocation?.rationGuid || allocationIndex}`,
          sequence: this.toNumber(item?.sequence),
          ration: allocation?.ration || item?.ration || "",
          percentage: this.toNumber(allocation?.percentage),
          feedAllocated,
          actualFed,
          variance: feedAllocated !== null || actualFed !== null ? (actualFed ?? 0) - (feedAllocated ?? 0) : null,
          active: Boolean(allocation?.active ?? item?.activeFeed),
        });
      });
    });

    return lodash.orderBy(rows, ["sequence", "ration"], ["asc", "asc"]);
  }

  private buildSessionSummary(rows: HistoryAllocationRow[]) {
    const percentages = Array.from(new Set(rows.map((item) => item.percentage).filter((item) => item !== null))) as number[];
    const ordered = percentages.sort((left, right) => left - right);
    return ordered.length ? ordered.map((item) => `${item.toFixed(0)}%`).join(" / ") : "";
  }

  private calculateDryMatterPct(
    callDry: number | null,
    callWet: number | null,
    fedDry: number | null,
    fedWet: number | null
  ): number | null {
    if (callDry !== null && callWet) return (callDry / callWet) * 100;
    if (fedDry !== null && fedWet) return (fedDry / fedWet) * 100;
    return null;
  }

  private lookupOwner(owner?: string) {
    if (!owner) return "";
    const found = this.owners.find((item) => item.guid === owner);
    return found?.name || owner;
  }

  private getRowKey(date: number, kraalId: string) {
    return `${date}-${kraalId}`;
  }

  private resolveDayDate(dayDate?: number | null, fallbackDate?: number | null) {
    const value = this.toNumber(dayDate) ?? this.toNumber(fallbackDate);
    if (!value) return 0;
    return moment(value).startOf("day").valueOf();
  }

  private getDateBoundary(value: string, endOfDay: boolean) {
    if (!value) return null;
    const parsed = moment(value, "YYYY-MM-DD", true);
    if (!parsed.isValid()) return null;
    return (endOfDay ? parsed.endOf("day") : parsed.startOf("day")).valueOf();
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private firstNumber(...values: any[]) {
    for (const value of values) {
      const numeric = this.toNumber(value);
      if (numeric !== null) return numeric;
    }
    return null;
  }
}
